import type {
  ForwardRefRenderFunction,
  MouseEventHandler,
  ReactNode,
} from 'react';

import {css} from '@emotion/react';
import {AnimatePresence, motion} from 'motion/react';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import React from 'react';

import {useAppTheme} from '../styles';

export type DialogProps = {
  children?: (props: DialogChildProps) => ReactNode;
};

/**
 * DialogRef provides imperative methods to control the Dialog component.
 * - close: Closes the dialog.
 * - open: Opens the dialog. Accepts an optional onClose callback.
 */
export type DialogRef = {
  close: () => void;
  open: (options?: {onClose: () => void}) => void;
};

type DialogChildProps = {
  onCloseClick: () => void;
};

/**
 * Dialog is a modal component that displays its children in a styled dialog overlay.
 * Supports imperative open/close via ref and closes automatically on large screens.
 *
 * @component
 * @param {DialogProps} props - Props for the Dialog component.
 * @param {React.Ref<DialogRef>} ref - Ref for imperative open/close methods.
 * @returns {JSX.Element} The rendered dialog overlay.
 */
const Dialog: ForwardRefRenderFunction<DialogRef, DialogProps> = (
  props: DialogProps,
  ref: React.Ref<DialogRef>,
): JSX.Element => {
  const {children} = props;
  const {screens} = useAppTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [onClose, setOnClose] = useState<() => void>(() => {});
  const dialogRef = useRef<HTMLDialogElement>(null);

  // Handle window width
  // Automatically close the dialog if the screen is large
  useEffect(() => {
    const mediaQuery = window.matchMedia(`(width >= ${screens.lg})`);

    const handleMediaQuery = (event: MediaQueryListEvent) => {
      if (event.matches && isOpen) setIsOpen(false);
    };

    mediaQuery.addEventListener('change', handleMediaQuery);

    return () => {
      mediaQuery.removeEventListener('change', handleMediaQuery);
    };
  }, [isOpen, screens.lg]);

  // Control the dialog open state
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    const handleDialogClose = () => {
      setIsOpen(false);
      onClose();
    };

    const open = () => {
      document.body.style.overflow = 'hidden';
      dialog.showModal();
      dialog.addEventListener('close', handleDialogClose);
    };

    const close = () => {
      dialog.close();
      dialog.removeEventListener('close', handleDialogClose);
      document.body.style.overflow = '';
    };

    if (isOpen) open();
    else close();

    return () => {
      close();
    };
  }, [isOpen, onClose]);

  const open: DialogRef['open'] = (options) => {
    if (options) {
      const {onClose} = options;
      setOnClose(() => onClose);
    }
    setIsOpen(true);
  };

  const close = () => {
    setIsOpen(false);
  };

  useImperativeHandle(ref, () => ({open, close}));

  const dialogCss = css({
    display: 'flex',
    flexDirection: 'column',
    inset: 0,
    maxWidth: 'unset',
    maxHeight: 'unset',
    width: '100%',
    height: '100%',
    padding: 0,
    position: 'fixed',
    zIndex: 100,
    border: 'none',
    backdropFilter: 'blur(5px)',
    backgroundColor: '#000000CC',
    margin: 0,
    overflowX: 'hidden',
  });

  const handleDialogClick: MouseEventHandler<HTMLDialogElement> = (event) => {
    const dialog = dialogRef.current;
    const target = event.target;
    if (dialog === target) setIsOpen(false);
  };

  const renderDialog = () => {
    if (!isOpen) return null;

    return (
      <motion.dialog
        animate={{opacity: 1}}
        css={dialogCss}
        exit={{opacity: 0}}
        initial={{opacity: 0}}
        onClick={handleDialogClick}
        ref={dialogRef}
      >
        {children?.({onCloseClick: close})}
      </motion.dialog>
    );
  };

  return <AnimatePresence>{renderDialog()}</AnimatePresence>;
};

export default forwardRef(Dialog);
